/*
 * Mae Jum recipe index filter — client-side, real-time filtering.
 * Pairs with snippets/blog-filter-sidebar.liquid and the blog index sections
 * (blog-template.liquid / new-blog-template.liquid).
 *
 * No dependencies. No localStorage. No URL params / history. No page reload.
 * Match data is read entirely from the card data-* attributes emitted by
 * snippets/article-card.liquid, so this behaves identically on UK and US stores.
 *
 * Card data attributes:
 *   data-t        title, lowercased
 *   data-ings     ingredient text, lowercased
 *   data-tags     raw article tags, comma-joined  (Allergies filter)
 *   data-products recipe_products handles, comma-joined
 *   data-diff     difficulty
 *   data-min      total minutes (integer; blank if unset)
 *   data-prot     protein types, comma-joined
 *   data-diet     dietary tags, comma-joined
 *   data-occ      occasion labels, comma-joined
 *   data-course   course
 *
 * Group logic (see snippets/blog-filter-sidebar.liquid data-f values):
 *   pro / diff / prot / occ / course / time : OR within group
 *   allergy (vs data-tags) / diet (vs data-diet)  : ALL-must-match
 *   With Ingredient  : data-ings must include ALL chips (partial)
 *   Without Ingredient : data-ings must include NONE of the chips (partial)
 *   Quick Search : substring vs data-t OR data-ings
 *   Between groups : AND
 */
(function () {
  'use strict';

  function splitLower(raw) {
    if (!raw) return [];
    return raw.split(',').map(function (s) { return s.trim().toLowerCase(); }).filter(Boolean);
  }

  // Card attribute backing each facet group (data-f value → card attribute).
  var GROUP_ATTR = {
    pro: 'data-products', prot: 'data-prot', occ: 'data-occ', course: 'data-course',
    diff: 'data-diff', diet: 'data-diet', allergy: 'data-tags'
  };
  // Parsed card property backing each group (used by updateCounts). Mirrors GROUP_ATTR
  // but points at the pre-split/lowercased arrays built in init().
  var GROUP_PROP = {
    pro: 'products', prot: 'prot', occ: 'occ', course: 'course',
    diff: 'diff', diet: 'diet', allergy: 'tags'
  };
  // Sections whose checkboxes are generated at runtime from the rendered cards.
  var DYNAMIC_FACETS = [
    { group: 'pro',    container: 'mj-filter-products', handle: true },
    { group: 'prot',   container: 'mj-filter-protein' },
    { group: 'occ',    container: 'mj-filter-occasion' },
    { group: 'course', container: 'mj-filter-course' }
  ];

  // Product handle → display label: "thai-green-curry-paste" → "Thai Green Curry Paste".
  function titleCaseHandle(handle) {
    return handle.split('-').map(function (w) {
      return w.length ? w.charAt(0).toUpperCase() + w.slice(1) : w;
    }).join(' ');
  }

  // Unique, original-case values found in `attr` across all cards (comma-split).
  function collectValues(attr, cardEls) {
    var seen = {}, out = [];
    cardEls.forEach(function (el) {
      (el.getAttribute(attr) || '').split(',').forEach(function (v) {
        v = v.trim();
        if (!v) return;
        var k = v.toLowerCase();
        if (!Object.prototype.hasOwnProperty.call(seen, k)) { seen[k] = true; out.push(v); }
      });
    });
    return out;
  }

  // Build the dynamic facet checkboxes once, before listeners are attached. Injected
  // markup matches the static pattern (label.mj-filter-option > input.mj-filter-cb
  // [data-f][data-v] + label span + count span) so the existing engine picks them up
  // via querySelectorAll('.mj-filter-cb'). data-products/-prot/-occ are comma-joined
  // in article-card.liquid, so values are always comma-split. Initial counts are set
  // by the first apply() → updateCounts() at the end of init (checkboxes are collected
  // after this injection, so counts can't be computed here yet).
  function buildDynamicFacets(cardEls) {
    DYNAMIC_FACETS.forEach(function (facet) {
      var container = document.getElementById(facet.container);
      if (!container) return;
      var values = collectValues(GROUP_ATTR[facet.group], cardEls).sort(function (a, b) {
        var la = a.toLowerCase(), lb = b.toLowerCase();
        return la < lb ? -1 : (la > lb ? 1 : 0);
      });
      if (!values.length) {
        // No card carries this attribute — hide the whole section wrapper.
        var section = container.closest('.mj-filter-section');
        if (section) section.style.display = 'none';
        return;
      }
      values.forEach(function (val) {
        var label = document.createElement('label');
        label.className = 'mj-filter-option';
        var input = document.createElement('input');
        input.type = 'checkbox';
        input.className = 'mj-filter-cb';
        input.setAttribute('data-f', facet.group);
        input.setAttribute('data-v', val);
        var text = document.createElement('span');
        text.textContent = facet.handle ? titleCaseHandle(val) : val;
        var count = document.createElement('span');
        count.className = 'mj-facet-count';
        label.appendChild(input);
        label.appendChild(text);
        label.appendChild(count);
        container.appendChild(label);
      });
    });
  }

  // Fill each card's hover overlay with its excerpt; cards with no excerpt get a
  // marker class so they receive the hover lift but no dark overlay. Runs once.
  function initHoverOverlays() {
    document.querySelectorAll('.m-article-card').forEach(function (card) {
      var excerpt = card.dataset.excerpt || '';
      if (excerpt.trim() === '') {
        card.classList.add('mj-card-no-excerpt');
      } else {
        var textEl = card.querySelector('.mj-card-overlay__text');
        if (textEl) textEl.textContent = excerpt;
      }
    });
  }

  function init() {
    var grid = document.getElementById('mj-article-grid');
    if (!grid) return;

    var resultsCount = document.getElementById('mj-results-count');
    var sbCount = document.getElementById('mj-sb-count');
    var filterRoot = document.getElementById('mj-filter');

    var cardEls = Array.prototype.slice.call(grid.querySelectorAll('.m-article-card'));
    var cards = cardEls.map(function (el) {
      var min = parseInt(el.getAttribute('data-min'), 10);
      return {
        el: el,
        t: (el.getAttribute('data-t') || '').toLowerCase(),
        ings: (el.getAttribute('data-ings') || '').toLowerCase(),
        tags: splitLower(el.getAttribute('data-tags')),
        products: splitLower(el.getAttribute('data-products')),
        diff: splitLower(el.getAttribute('data-diff')),
        prot: splitLower(el.getAttribute('data-prot')),
        diet: splitLower(el.getAttribute('data-diet')),
        occ: splitLower(el.getAttribute('data-occ')),
        course: splitLower(el.getAttribute('data-course')),
        min: isNaN(min) ? null : min
      };
    });

    function setCounts(n) {
      if (resultsCount) resultsCount.textContent = n + (n === 1 ? ' recipe' : ' recipes');
      if (sbCount) sbCount.textContent = n;
    }

    // No filter sidebar on this template (e.g. default blog) — just show the total.
    if (!filterRoot) { setCounts(cards.length); return; }

    // Inject the dynamic facets BEFORE collecting checkboxes, so the direct per-checkbox
    // listeners wired below cover both the static and the generated checkboxes.
    buildDynamicFacets(cardEls);
    initHoverOverlays();

    var searchInput = document.getElementById('mj-filter-search');
    var resetBtn = document.getElementById('mj-filter-reset');
    var checkboxes = Array.prototype.slice.call(filterRoot.querySelectorAll('.mj-filter-cb'));

    var wiList = [];
    var woList = [];

    // ---- helpers -----------------------------------------------------------
    function anyIn(sel, arr) {
      for (var i = 0; i < sel.length; i++) if (arr.indexOf(sel[i]) !== -1) return true;
      return false;
    }
    function allIn(sel, arr) {
      for (var i = 0; i < sel.length; i++) if (arr.indexOf(sel[i]) === -1) return false;
      return true;
    }
    function allPartial(needles, hay) {
      for (var i = 0; i < needles.length; i++) if (hay.indexOf(needles[i]) === -1) return false;
      return true;
    }
    function nonePartial(needles, hay) {
      for (var i = 0; i < needles.length; i++) if (hay.indexOf(needles[i]) !== -1) return false;
      return true;
    }
    function matchesTime(sel, min) {
      if (min === null) return false;
      for (var i = 0; i < sel.length; i++) {
        if (sel[i] === 'quick' && min <= 30) return true;
        if (sel[i] === 'hour' && min > 30 && min <= 60) return true;
        if (sel[i] === 'long' && min > 60) return true;
      }
      return false;
    }

    // Group checked boxes → { groupKey: [valueLower, ...] }
    function activeGroups() {
      var groups = {};
      checkboxes.forEach(function (cb) {
        if (!cb.checked) return;
        var g = cb.getAttribute('data-f');
        var v = (cb.getAttribute('data-v') || '').trim().toLowerCase();
        (groups[g] = groups[g] || []).push(v);
      });
      return groups;
    }

    function cardMatches(card, groups) {
      if (groups.pro && !anyIn(groups.pro, card.products)) return false;
      if (groups.diff && !anyIn(groups.diff, card.diff)) return false;
      if (groups.prot && !anyIn(groups.prot, card.prot)) return false;
      if (groups.occ && !anyIn(groups.occ, card.occ)) return false;
      if (groups.course && !anyIn(groups.course, card.course)) return false;
      if (groups.time && !matchesTime(groups.time, card.min)) return false;
      if (groups.allergy && !allIn(groups.allergy, card.tags)) return false;
      if (groups.diet && !allIn(groups.diet, card.diet)) return false;
      return true;
    }

    // ---- core apply --------------------------------------------------------
    function apply() {
      var search = (searchInput ? searchInput.value : '').trim().toLowerCase();
      var groups = activeGroups();
      var visible = 0;

      // Header label reflects whether any filter is active (checkbox, search or chip).
      // Re-queried each call (not cached at init) so it self-heals against any DOM
      // timing/re-render edge where the element wasn't present when init ran.
      var anyActive = search !== '' || wiList.length > 0 || woList.length > 0 ||
                      Object.keys(groups).length > 0;
      var resultsLabel = document.getElementById('mj-results-label');
      if (resultsLabel) resultsLabel.textContent = anyActive ? 'Search Results' : 'All Recipes';

      cards.forEach(function (card) {
        var show = true;
        if (search) show = card.t.indexOf(search) !== -1 || card.ings.indexOf(search) !== -1;
        if (show && wiList.length) show = allPartial(wiList, card.ings);
        if (show && woList.length) show = nonePartial(woList, card.ings);
        if (show) show = cardMatches(card, groups);

        card.el.style.display = show ? '' : 'none';
        if (show) visible++;
      });

      setCounts(visible);
      updateBadges(groups);
      updateCounts();
    }

    // ---- per-section count badges ------------------------------------------
    function updateBadges(groups) {
      var badges = filterRoot.querySelectorAll('.mj-filter-section__badge');
      Array.prototype.forEach.call(badges, function (badge) {
        var key = badge.getAttribute('data-badge');
        var n;
        if (key === 'wi') n = wiList.length;
        else if (key === 'wo') n = woList.length;
        else n = groups[key] ? groups[key].length : 0;
        badge.textContent = n;
        if (n > 0) badge.removeAttribute('hidden');
        else badge.setAttribute('hidden', '');
      });
    }

    // ---- per-checkbox live counts + dead-end grey-out -----------------------
    // For every checkbox (static AND dynamic), count how many currently-visible cards
    // carry that value in its backing attribute, and grey out unchecked zero-count
    // options so the user can never reach an empty result set. A checked box is never
    // disabled, even if its logical count is zero (per brief).
    function updateCounts() {
      var visible = cards.filter(function (c) { return c.el.style.display !== 'none'; });
      checkboxes.forEach(function (cb) {
        var g = cb.getAttribute('data-f');
        var v = (cb.getAttribute('data-v') || '').trim().toLowerCase();
        var n;
        if (g === 'time') {
          n = visible.filter(function (c) { return matchesTime([v], c.min); }).length;
        } else {
          var prop = GROUP_PROP[g];
          n = prop ? visible.filter(function (c) { return c[prop].indexOf(v) !== -1; }).length : 0;
        }
        var label = cb.closest('.mj-filter-option');
        var countEl = label ? label.querySelector('.mj-facet-count') : null;
        if (countEl) countEl.textContent = '(' + n + ')';
        if (n === 0 && !cb.checked) {
          if (label) label.classList.add('mj-facet-disabled');
          cb.disabled = true;
        } else {
          if (label) label.classList.remove('mj-facet-disabled');
          cb.disabled = false;
        }
      });
    }

    // ---- With / Without ingredient chips -----------------------------------
    function chipContainer(type) {
      return document.getElementById(type === 'wi' ? 'mj-wi-chips' : 'mj-wo-chips');
    }
    function listFor(type) { return type === 'wi' ? wiList : woList; }

    function renderChip(type, value) {
      var cont = chipContainer(type);
      if (!cont) return;
      var chip = document.createElement('span');
      chip.className = 'mj-' + type + '-chip';
      chip.setAttribute('data-v', value);
      var label = document.createElement('span');
      label.className = 'mj-' + type + '-chip__label';
      label.textContent = value;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'mj-' + type + '-chip__remove';
      btn.setAttribute('aria-label', 'Remove ' + value);
      btn.innerHTML = '&times;';
      btn.addEventListener('click', function () { removeIngredient(type, value); });
      chip.appendChild(label);
      chip.appendChild(btn);
      cont.appendChild(chip);
    }

    function addIngredient(type, value) {
      value = (value || '').trim().toLowerCase();
      if (!value) return;
      var list = listFor(type);
      if (list.indexOf(value) !== -1) return; // no duplicates
      list.push(value);
      renderChip(type, value);
      apply();
    }

    function removeIngredient(type, value) {
      var list = listFor(type);
      var i = list.indexOf(value);
      if (i !== -1) list.splice(i, 1);
      var cont = chipContainer(type);
      if (cont) {
        Array.prototype.slice.call(cont.children).forEach(function (chip) {
          if (chip.getAttribute('data-v') === value) cont.removeChild(chip);
        });
      }
      apply();
    }

    function wireIngredient(type) {
      var input = document.getElementById(type === 'wi' ? 'mj-wi-input' : 'mj-wo-input');
      var addBtn = filterRoot.querySelector('button[data-add="' + type + '"]');
      if (input) {
        input.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.keyCode === 13) {
            e.preventDefault();
            addIngredient(type, input.value);
            input.value = '';
          }
        });
      }
      if (addBtn) {
        addBtn.addEventListener('click', function () {
          if (input) { addIngredient(type, input.value); input.value = ''; }
        });
      }
    }

    // ---- listeners ---------------------------------------------------------
    if (searchInput) searchInput.addEventListener('input', apply);
    checkboxes.forEach(function (cb) { cb.addEventListener('change', apply); });
    wireIngredient('wi');
    wireIngredient('wo');

    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        checkboxes.forEach(function (cb) { cb.checked = false; });
        if (searchInput) searchInput.value = '';
        wiList.length = 0;
        woList.length = 0;
        var wic = chipContainer('wi'); if (wic) wic.innerHTML = '';
        var woc = chipContainer('wo'); if (woc) woc.innerHTML = '';
        apply();
      });
    }

    // Collapsible sections — toggle aria-expanded (state in the DOM attribute
    // only; nothing persisted). Chevron rotation is CSS-driven off aria-expanded.
    var headers = filterRoot.querySelectorAll('.mj-filter-section__header');
    Array.prototype.forEach.call(headers, function (header) {
      header.addEventListener('click', function () {
        var section = header.closest('.mj-filter-section');
        if (!section) return;
        var open = section.getAttribute('aria-expanded') !== 'false';
        section.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });

    // Initial paint (full set; no filters active yet).
    apply();

    // ---- TRC-012 Steps 4/5: mobile filter drawer -----------------------------
    // Open/close the off-canvas drawer, keep the mobile-bar badge in sync, and
    // hide the fixed bar over the footer. Drawer chrome lives OUTSIDE #mj-filter
    // (in the section), so query via document — not filterRoot. All drawer
    // interactions are mobile-only (guard on innerWidth < 768).
    var mjDrawer   = document.querySelector('.m-blog-sidebar');
    var mjOverlay  = document.getElementById('mj-filter-overlay');
    var mjOpenBtn  = document.getElementById('mj-filter-open-btn');
    var mjCloseBtn = document.getElementById('mj-filter-close-btn');
    var mjBar      = document.querySelector('.mj-filter-mobile-bar');
    var mjBadge    = document.getElementById('mj-filter-badge');

    function mjOpenDrawer() {
      if (window.innerWidth >= 768) return;
      if (mjDrawer) mjDrawer.classList.add('mj-drawer-open');
      if (mjOverlay) mjOverlay.classList.add('mj-overlay-active');
      if (mjOpenBtn) mjOpenBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function mjCloseDrawer() {
      if (window.innerWidth >= 768) return;
      if (mjDrawer) mjDrawer.classList.remove('mj-drawer-open');
      if (mjOverlay) mjOverlay.classList.remove('mj-overlay-active');
      if (mjOpenBtn) mjOpenBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    if (mjOpenBtn) mjOpenBtn.addEventListener('click', mjOpenDrawer);
    if (mjCloseBtn) mjCloseBtn.addEventListener('click', mjCloseDrawer);
    if (mjOverlay) mjOverlay.addEventListener('click', mjCloseDrawer);
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.keyCode !== 27) return;
      if (window.innerWidth >= 768) return;
      if (mjDrawer && mjDrawer.classList.contains('mj-drawer-open')) mjCloseDrawer();
    });

    // Active-filter count on the mobile bar. Delegated change listener so the
    // runtime-injected facet checkboxes are covered too.
    function updateBadge() {
      if (!mjBadge) return;
      var n = filterRoot.querySelectorAll('.mj-filter-cb:checked').length;
      if (n > 0) {
        mjBadge.textContent = n;
        mjBadge.style.display = 'flex';
      } else {
        mjBadge.style.display = 'none';
      }
    }
    filterRoot.addEventListener('change', updateBadge);
    updateBadge();

    // Hide the fixed Filters bar once the footer scrolls into view.
    var mjFooter = document.querySelector('.m-footer');
    if (mjFooter && mjBar && window.innerWidth < 768 && 'IntersectionObserver' in window) {
      var mjBarObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          mjBar.style.display = entry.isIntersecting ? 'none' : '';
        });
      });
      mjBarObserver.observe(mjFooter);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ------------------------------------------------------------------
 * Bug 1 (sticky sidebar): CSS position:sticky does not engage in this
 * theme at runtime (confirmed via a top:150px diagnostic — no effect at
 * any offset). JS fallback: fix the sidebar to the viewport once its
 * placeholder position scrolls past the header offset, restoring flow on
 * scroll-up / resize. A placeholder preserves the sidebar's footprint so
 * the grid column doesn't reflow when the sidebar is taken out of flow.
 * ------------------------------------------------------------------ */
(function() {
  var sidebar = document.querySelector('.m-blog-sidebar');
  if (!sidebar) return;

  var placeholder = document.createElement('div');
  var isFixed = false;
  var triggerY = 0;
  var headerEl = document.querySelector('.m-header')
                 || document.querySelector('header')
                 || document.querySelector('.header');

  function initPlaceholder() {
    placeholder.style.width  = sidebar.offsetWidth + 'px';
    placeholder.style.height = sidebar.offsetHeight + 'px';
    placeholder.style.display = 'none';
    sidebar.parentNode.insertBefore(placeholder, sidebar);
    // Trigger point measured ONCE (stable across scroll). The placeholder is
    // display:none here (→ zero rect), so measure the in-flow sidebar, which sits
    // at the placeholder's natural position and gives the intended value.
    var rect = sidebar.getBoundingClientRect();
    triggerY = rect.top + window.scrollY;
  }

  function onScroll() {
    // TRC-012 Fix 2: drawer mode owns mobile — never engage the desktop fix here.
    if (window.innerWidth < 768) return;
    var offset = 40;
    var placeholderTop = triggerY - offset;

    var container = sidebar.closest('.m-row')
                    || sidebar.parentNode;
    var containerBottom = container.getBoundingClientRect().bottom
                          + window.scrollY;
    var sidebarBottom = window.scrollY + offset
                        + sidebar.offsetHeight;

    if (!isFixed && window.scrollY > placeholderTop
        && sidebarBottom < containerBottom) {
      // switch to fixed
      sidebar.style.position = 'fixed';
      sidebar.style.top = offset + 'px';
      sidebar.style.bottom = '';
      sidebar.style.width = placeholder.style.width;
      sidebar.style.maxHeight = 'calc(100vh - ' + offset + 'px - 20px)';
      sidebar.style.overflowY = 'auto';
      placeholder.style.display = 'block';
      isFixed = true;
    } else if (isFixed && sidebarBottom >= containerBottom) {
      // park at bottom
      sidebar.style.position = 'absolute';
      sidebar.style.top = '';
      sidebar.style.bottom = '0';
      sidebar.style.width = placeholder.style.width;
      sidebar.style.maxHeight = '';
      sidebar.style.overflowY = '';
      placeholder.style.display = 'block';
      isFixed = false;
    } else if (isFixed && window.scrollY <= placeholderTop) {
      // release at top
      sidebar.style.position = '';
      sidebar.style.top = '';
      sidebar.style.bottom = '';
      sidebar.style.width = '';
      sidebar.style.maxHeight = '';
      sidebar.style.overflowY = '';
      placeholder.style.display = 'none';
      isFixed = false;
    }
  }

  function onResize() {
    // TRC-012 Fix 2: below 768 the sidebar is a CSS drawer, so fully unwind any
    // desktop sticky state left over from a wider load — clear the inline styles,
    // hide the placeholder, and bail before re-arming onScroll (which would re-fix).
    if (window.innerWidth < 768) {
      sidebar.style.position = '';
      sidebar.style.top = '';
      sidebar.style.bottom = '';
      sidebar.style.width = '';
      sidebar.style.maxHeight = '';
      sidebar.style.overflowY = '';
      placeholder.style.display = 'none';
      isFixed = false;
      return;
    }
    if (isFixed) {
      sidebar.style.position = '';
      sidebar.style.top      = '';
      sidebar.style.width    = '';
      sidebar.style.maxHeight = '';
      sidebar.style.overflowY = '';
      placeholder.style.display = 'none';
      isFixed = false;
    }
    var rect = sidebar.getBoundingClientRect();
    triggerY = rect.top + window.scrollY;
    placeholder.style.width = sidebar.offsetWidth + 'px';
    onScroll();
  }

  document.addEventListener('DOMContentLoaded', function() {
    // Desktop only — mobile gets a separate filter drawer in a future session.
    if (window.innerWidth < 768) return;
    initPlaceholder();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    onScroll();
  });
})();
